import { useEffect, useState, type ReactNode } from 'react';
import { FEATURE_FLAG_KEYS, flagDefault } from '@thirdsectorbee/feature-flags';

import { getFlagClient } from './client';
import { defaultFlagState, FeatureFlagContext, type FeatureFlagState } from './context';

// Resolves the ConfigCat flags for this page (ADR-045).
//
// EVALUATED ANONYMOUSLY, deliberately. This renderer serves a public form: there
// is no logged-in user, and the URL carries a tenant SLUG, not a tenant id. The
// admin app's targeting attributes are `tenantId` and `tenantName`, so sending a
// slug under either name would make a dashboard rule written once behave
// differently on this surface — matching in the app and silently never matching
// here, which is worse than not targeting at all. So no user object is sent, and
// only GLOBAL rules (including a kill switch) apply here. If per-tenant
// targeting is ever needed on this page, give it its own attribute name rather
// than reusing one that means something else.
//
// Like the admin app's provider it NEVER blocks render: a release flag's answer
// when ConfigCat is unreachable is the same as its answer when ConfigCat says
// "off" — the deployed behaviour. Blocking would put ConfigCat on the critical
// path of a donation form in exchange for nothing.
function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlagState>(defaultFlagState);

  useEffect(() => {
    const client = getFlagClient();
    if (!client) {
      return;
    }
    let cancelled = false;
    void Promise.all(
      FEATURE_FLAG_KEYS.map((key) =>
        // Promise.resolve().then(...) so a SYNCHRONOUS throw from inside the SDK
        // becomes a rejection the catch can absorb, rather than taking down the
        // whole Promise.all and leaving every other flag unresolved.
        Promise.resolve()
          .then(() => client.getValueAsync(key, flagDefault(key), undefined))
          .catch(() => flagDefault(key))
          .then((value) => [key, value] as const),
      ),
    ).then((entries) => {
      if (!cancelled) {
        setFlags(Object.fromEntries(entries) as FeatureFlagState);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return <FeatureFlagContext value={flags}>{children}</FeatureFlagContext>;
}

export default FeatureFlagProvider;
